import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  createApp,
  deleteApp,
  getMe,
  isProRequiredError,
  listAppTools,
  listApps,
  type App,
} from "../lib/api";
import { ConnectCard } from "../components/ConnectCard";
import { MBBtn, MBChip, MBDot, MBIcon } from "../components/mb";

const RESERVED = new Set(["__inbox", "examples"]);

export function AppsList() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<"recent" | "name" | "widgets">("recent");
  const [view, setView] = useState<"list" | "grid">("list");

  const me = useQuery({ queryKey: ["me"], queryFn: getMe, retry: false });
  const tier = me.data?.tier ?? "free";

  const { data, isLoading, error } = useQuery({ queryKey: ["apps"], queryFn: listApps, retry: false });
  const apps = data ?? [];
  const visible = apps.filter((a) => a.slug !== "__inbox");
  const ownProjects = apps.filter((a) => !RESERVED.has(a.slug));
  const hasExamples = apps.some((a) => a.slug === "examples");
  const inbox = apps.find((a) => a.slug === "__inbox");

  // Per-project widget counts (parallel, cached). Powers the stats strip + rows.
  const countQueries = useQueries({
    queries: apps.map((a) => ({
      queryKey: ["app-tools", a.slug],
      queryFn: () => listAppTools(a.slug),
      enabled: !!data,
      retry: false,
    })),
  });
  const countBySlug = useMemo(() => {
    const m: Record<string, number> = {};
    apps.forEach((a, i) => {
      const d = countQueries[i]?.data;
      if (d) m[a.slug] = d.filter((t) => t.renderable !== false).length;
    });
    return m;
  }, [apps, countQueries]);
  const inboxCount = inbox ? countBySlug[inbox.slug] ?? 0 : 0;
  const totalWidgets = visible.reduce((s, a) => s + (countBySlug[a.slug] ?? 0), 0);
  const publishedCount = visible.filter((a) => a.public).length;

  const remove = useMutation({
    mutationFn: (slug: string) => deleteApp(slug),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps"] }),
  });

  const create = useMutation({
    mutationFn: (name: string) => createApp({ name }),
    onSuccess: (app) => {
      qc.invalidateQueries({ queryKey: ["apps"] });
      nav(`/projects/${app.slug}`);
    },
    onError: (e) => {
      if (isProRequiredError(e)) {
        window.alert(e.message);
        nav("/settings#billing");
      } else {
        window.alert(e instanceof Error ? e.message : "Could not create project.");
      }
    },
  });

  const onNewProject = () => {
    const name = window.prompt("New project name");
    if (name && name.trim()) create.mutate(name.trim());
  };

  let rows = visible.filter((a) => !filter || a.name.toLowerCase().includes(filter.toLowerCase()) || a.slug.includes(filter.toLowerCase()));
  if (sort === "name") rows = [...rows].sort((a, b) => a.name.localeCompare(b.name));
  if (sort === "widgets") rows = [...rows].sort((a, b) => (countBySlug[b.slug] ?? 0) - (countBySlug[a.slug] ?? 0));
  if (sort === "recent") rows = [...rows].sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""));

  return (
    <div style={{ maxWidth: 1320, margin: "0 auto", padding: "32px 28px 96px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, flexWrap: "wrap", marginBottom: 28 }}>
        <div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, letterSpacing: "0.08em", color: "var(--fg-4)", textTransform: "uppercase", marginBottom: 8 }}>
            workspace{me.data?.email ? ` · ${me.data.email.split("@")[0]}` : ""}
          </div>
          <h1 style={{ margin: 0, fontFamily: "var(--font-sans)", fontSize: 32, lineHeight: "36px", letterSpacing: "-0.02em", fontWeight: 600, color: "var(--fg-1)" }}>
            Your projects
          </h1>
          <p style={{ margin: "8px 0 0", fontFamily: "var(--font-sans)", fontSize: 14, lineHeight: "20px", color: "var(--fg-3)", maxWidth: 560 }}>
            Each project is a folder of saved widgets and its own MCP endpoint. Publish one to share it publicly.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Link to="/projects/__inbox">
            <MBBtn variant="outline" size="md" icon={<MBIcon name="inbox" size={14} />}>Open Inbox</MBBtn>
          </Link>
          <MBBtn variant="primary" size="md" icon={<MBIcon name="plus" size={14} />} onClick={onNewProject} disabled={create.isPending}>
            {create.isPending ? "Creating…" : "New project"}
          </MBBtn>
        </div>
      </div>

      {isLoading && <div style={{ color: "var(--fg-2)", fontSize: 14 }}>Loading…</div>}

      {error instanceof ApiError && error.status === 401 && (
        <div style={{ color: "var(--fg-2)", fontSize: 14 }}>
          You aren't signed in. Visit <Link to="/login" style={{ color: "var(--accent-fg)" }}>/login</Link> to request a magic link.
        </div>
      )}
      {error && !(error instanceof ApiError && error.status === 401) && (
        <div style={{ color: "var(--danger)", fontSize: 13, fontFamily: "var(--font-mono)" }}>
          {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {data && (
        <>
          {/* Stats strip */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              background: "var(--surface-1)",
              border: "1px solid var(--line-2)",
              borderRadius: 10,
              marginBottom: 28,
              overflow: "hidden",
            }}
            className="mb-stats"
          >
            <Stat label="projects" value={ownProjects.length} hint={tier === "free" ? `${Math.max(0, 1 - ownProjects.length)} free slot left` : "unlimited"} accentHint={tier === "free"} />
            <Stat label="widgets" value={totalWidgets} hint="across all projects" />
            <Stat label="published" value={publishedCount} hint={publishedCount === 1 ? "public endpoint" : "public endpoints"} />
            <Stat
              label="plan"
              value={tier[0].toUpperCase() + tier.slice(1)}
              hint={tier === "free" ? "upgrade to Pro →" : "you're all set"}
              accentHint={tier === "free"}
              hintTo={tier === "free" ? "/settings#billing" : undefined}
            />
          </div>

          {/* Inbox callout */}
          <Link
            to="/projects/__inbox"
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr auto auto",
              gap: 18,
              alignItems: "center",
              padding: "16px 20px",
              marginBottom: 28,
              background: "linear-gradient(90deg, rgba(59,130,255,0.08), rgba(59,130,255,0.02))",
              border: "1px solid var(--accent-line)",
              borderRadius: 10,
            }}
          >
            <span style={{ width: 40, height: 40, borderRadius: 8, background: "var(--surface-2)", border: "1px solid var(--line-2)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--accent-fg)" }}>
              <MBIcon name="inbox" size={20} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontFamily: "var(--font-sans)", fontSize: 16, fontWeight: 600, color: "var(--fg-1)" }}>Inbox</div>
                {inboxCount > 0 && <MBChip accent>{inboxCount} unsaved</MBChip>}
              </div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-3)", marginTop: 4 }}>
                __inbox · widgets you've created but haven't filed into a project
              </div>
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-4)", whiteSpace: "nowrap" }}>
              {inbox?.updatedAt ? rel(inbox.updatedAt) : ""}
            </div>
            <MBIcon name="chevron" size={16} color="var(--fg-3)" />
          </Link>

          {ownProjects.length === 0 ? (
            <GettingStarted hasExamples={hasExamples} />
          ) : (
            <>
              {/* Toolbar */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 12px", height: 36, background: "var(--surface-1)", border: "1px solid var(--line-2)", borderRadius: 8, minWidth: 280, flex: "1 1 280px", maxWidth: 480 }}>
                  <MBIcon name="search" size={14} color="var(--fg-3)" />
                  <input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter projects by name or slug…"
                    style={{ flex: 1, background: "transparent", border: 0, color: "var(--fg-1)", fontFamily: "var(--font-sans)", fontSize: 14, outline: "none" }}
                  />
                  {filter && (
                    <button onClick={() => setFilter("")} style={{ background: "transparent", border: 0, color: "var(--fg-4)", cursor: "pointer", display: "inline-flex", padding: 2 }}>
                      <MBIcon name="x" size={13} />
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Segmented
                    label="sort"
                    value={sort}
                    onChange={(v) => setSort(v as typeof sort)}
                    options={[["recent", "Recent"], ["name", "Name"], ["widgets", "Widgets"]]}
                  />
                  <div style={{ display: "flex", border: "1px solid var(--line-2)", borderRadius: 6, background: "var(--surface-1)", padding: 2 }}>
                    {(["list", "grid"] as const).map((k) => (
                      <button
                        key={k}
                        onClick={() => setView(k)}
                        style={{ width: 30, height: 26, background: view === k ? "var(--surface-3)" : "transparent", color: view === k ? "var(--fg-1)" : "var(--fg-3)", border: 0, borderRadius: 4, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <MBIcon name={k} size={14} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {view === "list" ? (
                <ProjectList rows={rows} counts={countBySlug} onDelete={(slug, name) => confirmDelete(slug, name, remove.mutate)} />
              ) : (
                <ProjectGrid rows={rows} counts={countBySlug} />
              )}

              {/* Bottom hint */}
              <div style={{ marginTop: 32, padding: "20px 24px", background: "var(--surface-1)", border: "1px dashed var(--line-2)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <span style={{ width: 36, height: 36, borderRadius: 8, background: "var(--surface-2)", border: "1px solid var(--line-2)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--accent-fg)" }}>
                    <MBIcon name="sparkles" size={18} />
                  </span>
                  <div>
                    <div style={{ fontFamily: "var(--font-sans)", fontSize: 14.5, fontWeight: 500, color: "var(--fg-1)" }}>Need inspiration?</div>
                    <div style={{ fontFamily: "var(--font-sans)", fontSize: 13, color: "var(--fg-3)", marginTop: 2 }}>
                      Grab ready-made prompts — dashboards, calculators, status boards — and paste them into Claude or ChatGPT.
                    </div>
                  </div>
                </div>
                <Link to="/settings">
                  <MBBtn variant="outline" size="md" icon={<MBIcon name="arrow" size={13} />}>Browse prompts</MBBtn>
                </Link>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function confirmDelete(slug: string, name: string, mutate: (slug: string) => void) {
  if (window.confirm(`Delete project "${name}" and all of its widgets? This can't be undone.`)) mutate(slug);
}

function rel(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const min = Math.round((Date.now() - t) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.round(day / 7);
  if (wk < 5) return `${wk}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function Stat({
  label,
  value,
  hint,
  accentHint = false,
  hintTo,
}: {
  label: string;
  value: ReactNode;
  hint: string;
  accentHint?: boolean;
  hintTo?: string;
}) {
  const hintEl = (
    <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: accentHint ? "var(--accent-fg)" : "var(--fg-4)" }}>{hint}</div>
  );
  return (
    <div style={{ padding: "18px 22px", borderRight: "1px solid var(--line-1)", display: "flex", flexDirection: "column", gap: 6 }} className="mb-stat-cell">
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.08em", color: "var(--fg-4)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontFamily: "var(--font-sans)", fontSize: 28, fontWeight: 600, color: "var(--fg-1)", letterSpacing: "-0.02em", lineHeight: 1 }}>{value}</div>
      {hintTo ? <Link to={hintTo}>{hintEl}</Link> : hintEl}
    </div>
  );
}

function Segmented({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-4)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
      <div style={{ display: "flex", border: "1px solid var(--line-2)", borderRadius: 6, background: "var(--surface-1)", padding: 2 }}>
        {options.map(([k, l]) => (
          <button
            key={k}
            onClick={() => onChange(k)}
            style={{ padding: "5px 10px", background: value === k ? "var(--surface-3)" : "transparent", color: value === k ? "var(--fg-1)" : "var(--fg-3)", border: 0, borderRadius: 4, cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 12.5, fontWeight: 500 }}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ pub }: { pub: boolean }) {
  const c = pub ? "var(--success)" : "var(--fg-4)";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        color: c,
        padding: "2px 8px",
        border: `1px solid ${pub ? "rgba(61,207,142,0.32)" : "var(--line-2)"}`,
        background: pub ? "var(--success-soft)" : "var(--surface-2)",
        borderRadius: 999,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        whiteSpace: "nowrap",
      }}
    >
      <MBDot color={c} />
      {pub ? "public" : "private"}
    </span>
  );
}

function ProjectIcon({ icon }: { icon?: string | null }) {
  return (
    <span style={{ width: 40, height: 40, borderRadius: 8, background: "var(--surface-2)", border: "1px solid var(--line-2)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "var(--accent-fg)" }}>
      {icon ?? "🧩"}
    </span>
  );
}

function ProjectList({
  rows,
  counts,
  onDelete,
}: {
  rows: App[];
  counts: Record<string, number>;
  onDelete: (slug: string, name: string) => void;
}) {
  return (
    <div style={{ background: "var(--surface-1)", border: "1px solid var(--line-2)", borderRadius: 10, overflow: "hidden" }}>
      <div
        className="mb-proj-row"
        style={{ padding: "10px 20px", borderBottom: "1px solid var(--line-2)", fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--fg-4)" }}
      >
        <div>name</div>
        <div>widgets</div>
        <div>status</div>
        <div>updated</div>
        <div></div>
      </div>
      {rows.map((p, i) => (
        <div
          key={p.slug}
          className="mb-proj-row mb-proj-link"
          style={{ borderTop: i ? "1px solid var(--line-1)" : 0 }}
        >
          <Link to={`/projects/${p.slug}`} style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, padding: "14px 0 14px 20px", color: "var(--fg-1)" }}>
            <ProjectIcon icon={p.icon} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 500, color: "var(--fg-1)", letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-4)", marginTop: 3 }}>
                mcpbuild.app/<span style={{ color: "var(--fg-3)" }}>{p.slug}</span>
              </div>
            </div>
          </Link>
          <Link to={`/projects/${p.slug}`} style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--fg-2)", display: "flex", alignItems: "center" }}>
            {counts[p.slug] ?? "—"}
          </Link>
          <Link to={`/projects/${p.slug}`} style={{ display: "flex", alignItems: "center" }}>
            <StatusBadge pub={!!p.public} />
          </Link>
          <Link to={`/projects/${p.slug}`} style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-4)", display: "flex", alignItems: "center" }}>
            {p.updatedAt ? rel(p.updatedAt) : ""}
          </Link>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 16, gap: 6 }}>
            <button
              onClick={() => onDelete(p.slug, p.name)}
              title="Delete project"
              className="mb-row-trash"
              style={{ background: "transparent", border: 0, color: "var(--fg-4)", cursor: "pointer", padding: 4, display: "inline-flex" }}
            >
              <MBIcon name="trash" size={14} />
            </button>
            <MBIcon name="chevron" size={14} color="var(--fg-4)" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ProjectGrid({ rows, counts }: { rows: App[]; counts: Record<string, number> }) {
  return (
    <div className="mb-usecase-grid">
      {rows.map((p) => (
        <Link
          key={p.slug}
          to={`/projects/${p.slug}`}
          style={{ display: "flex", flexDirection: "column", gap: 12, padding: "20px 22px", background: "var(--surface-1)", border: "1px solid var(--line-2)", borderRadius: 10, color: "var(--fg-1)" }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <ProjectIcon icon={p.icon} />
            <StatusBadge pub={!!p.public} />
          </div>
          <div>
            <div style={{ fontFamily: "var(--font-sans)", fontSize: 16, fontWeight: 600, color: "var(--fg-1)", letterSpacing: "-0.01em" }}>{p.name}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-4)", marginTop: 4 }}>
              mcpbuild.app/<span style={{ color: "var(--fg-3)" }}>{p.slug}</span>
            </div>
          </div>
          <div style={{ height: 1, background: "var(--line-1)", marginTop: 4 }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-4)" }}>
            <span>{counts[p.slug] ?? 0} widgets</span>
            <span>{p.updatedAt ? rel(p.updatedAt) : ""}</span>
          </div>
        </Link>
      ))}
    </div>
  );
}

// Empty state — kept from the original flow, restyled to the new tokens.
function GettingStarted({ hasExamples }: { hasExamples: boolean }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://mcpbuild.app";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ borderRadius: 10, border: "1px solid var(--line-2)", background: "var(--surface-1)", padding: 20 }}>
        <h2 style={{ margin: 0, fontFamily: "var(--font-sans)", fontSize: 16, fontWeight: 600, color: "var(--fg-1)" }}>New here? Here's how it works</h2>
        <p style={{ margin: "8px 0 0", color: "var(--fg-3)", fontSize: 14 }}>
          {hasExamples
            ? "We've added an Examples project below — open it to see what widgets look like. To make your own, MCPBuild turns React widgets you generate in Claude into reusable, callable MCP tools:"
            : "MCPBuild turns React widgets you generate inside Claude into reusable, callable MCP tools. Three steps:"}
        </p>
      </div>
      <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 16 }}>
        <li style={{ display: "flex", gap: 14 }}>
          <StepNum n={1} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 600, color: "var(--fg-1)" }}>Connect the builder to Claude</div>
            <p style={{ margin: "4px 0 10px", color: "var(--fg-3)", fontSize: 14 }}>
              The workshop endpoint — it gives Claude the <code style={{ fontFamily: "var(--font-mono)" }}>dynamic_widget</code>,{" "}
              <code style={{ fontFamily: "var(--font-mono)" }}>add_widget_to_project</code>, and <code style={{ fontFamily: "var(--font-mono)" }}>list_widgets</code> tools.
            </p>
            <ConnectCard url={`${origin}/mcp`} title="Builder endpoint" />
          </div>
        </li>
        <li style={{ display: "flex", gap: 14 }}>
          <StepNum n={2} />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 600, color: "var(--fg-1)" }}>Generate a widget</div>
            <p style={{ margin: "4px 0 0", color: "var(--fg-3)", fontSize: 14 }}>
              Ask Claude <em>"use dynamic_widget to render a bar chart of these numbers…"</em> — it renders inline and lands in your Inbox.
              Grab ready-made prompts on the <Link to="/settings" style={{ color: "var(--accent-fg)" }}>Settings</Link> page.
            </p>
          </div>
        </li>
        <li style={{ display: "flex", gap: 14 }}>
          <StepNum n={3} />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 600, color: "var(--fg-1)" }}>Add it to a project &amp; connect that</div>
            <p style={{ margin: "4px 0 0", color: "var(--fg-3)", fontSize: 14 }}>
              Ask Claude to <code style={{ fontFamily: "var(--font-mono)" }}>add_widget_to_project</code> (or use Save to project in the Inbox). Each project
              becomes its own MCP endpoint — <code style={{ fontFamily: "var(--font-mono)" }}>{origin}/mcp/&lt;project&gt;</code>.
            </p>
          </div>
        </li>
      </ol>
    </div>
  );
}

function StepNum({ n }: { n: number }) {
  return (
    <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: "50%", background: "var(--surface-2)", border: "1px solid var(--line-2)", color: "var(--fg-1)", display: "grid", placeItems: "center", fontSize: 13, fontFamily: "var(--font-mono)" }}>
      {n}
    </div>
  );
}
